import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';

import styles from './styles.module.css';

function Switch({
  active,
  blurredOnInactive,
  disabled,
  label,
  labelPosition,
  labelGap,
  onSwitch,
  text,
  title,
}) {
  const [isActiveSwitch, setIsActiveSwitch] = useState(active);

  const switchDivClassName = {
    Right: styles.switchDivRight,
    Left: styles.switchDivLeft,
    Top: styles.switchDivTop,
    Bottom: styles.switchDivBottom,
  }[labelPosition] || styles.switchDivRight;

  const labelClassName = {
    Right: styles.labelRight,
    Left: styles.labelLeft,
    Top: styles.labelTop,
    Bottom: styles.labelBottom,
  }[labelPosition] || styles.labelRight;

  // The gap sits on the side of the label that faces the switch button,
  // so it depends on where the label is positioned.
  const labelGapMarginProperty = {
    Right: 'marginLeft',
    Left: 'marginRight',
    Top: 'marginBottom',
    Bottom: 'marginTop',
  }[labelPosition] || 'marginLeft';

  const labelStyle = labelGap != null
    ? { [labelGapMarginProperty]: typeof labelGap === 'number' ? `${labelGap}px` : labelGap }
    : undefined;

  useEffect(() => {
    setIsActiveSwitch(active);
  }, [active]);

  const executeOnSwitch = () => {
    if (!disabled) {
      if (typeof onSwitch === 'function') onSwitch(!isActiveSwitch);
      setIsActiveSwitch(!isActiveSwitch);
    }
  };

  return (
    <div className={clsx(styles.switchDiv, switchDivClassName)} title={title}>
      {text ? (
        <div>
          {isActiveSwitch ? (
            <div
              className={clsx(styles.switch, styles.switchWithText, {
                [styles.disabledActiveSwitch]: disabled && isActiveSwitch,
                [styles.disabledSwitch]: disabled && !isActiveSwitch,
              })}
              onClick={executeOnSwitch}
              onKeyDown={executeOnSwitch}
              role="presentation"
            >
              <span
                className={clsx(styles.text, {
                  [styles.disabledLabel]: blurredOnInactive,
                })}
              >
                {text[0]}
              </span>
            </div>
          ) : (
            <div
              className={clsx(styles.activeSwitch, styles.activeSwitchWithText, {
                [styles.disabledActiveSwitch]: disabled && isActiveSwitch,
                [styles.disabledSwitch]: disabled && !isActiveSwitch,
              })}
              onClick={executeOnSwitch}
              onKeyDown={executeOnSwitch}
              role="presentation"
            >
              <span className={styles.activeText}>{text && text[1]}</span>
            </div>
          )}
        </div>
      ) : (
        <div
          className={clsx({
            [styles.activeSwitch]: isActiveSwitch,
            [styles.switch]: !isActiveSwitch,
            [styles.disabledActiveSwitch]: disabled && isActiveSwitch,
            [styles.disabledSwitch]: disabled && !isActiveSwitch,
          })}
          onClick={executeOnSwitch}
          onKeyDown={executeOnSwitch}
          role="presentation"
        />
      )}
      {label && (
        <div
          className={clsx(styles.label, labelClassName, {
            [styles.disabledLabel]: blurredOnInactive,
          })}
          style={labelStyle}
        >
          {label}
        </div>
      )}
    </div>
  );
}

Switch.propTypes = {
  active: PropTypes.bool,
  blurredOnInactive: PropTypes.bool,
  disabled: PropTypes.bool,
  label: PropTypes.string,
  labelGap: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  labelPosition: PropTypes.oneOf(['Right', 'Left', 'Top', 'Bottom']),
  onSwitch: PropTypes.func,
  text: PropTypes.arrayOf([PropTypes.string]),
  title: PropTypes.string,
};

Switch.defaultProps = {
  active: false,
  blurredOnInactive: false,
  disabled: false,
  label: undefined,
  labelGap: undefined,
  labelPosition: 'Right',
  onSwitch: undefined,
  text: undefined,
  title: undefined,
};

export default Switch;
